import Position from './Position';
var PADDING = 5;
var WIDTH_WEIGHT = 0.3;
function getBoundingBox(elementOrBox) {
    var legacyBox;
    if (elementOrBox instanceof HTMLElement) {
        legacyBox = elementOrBox.getBoundingClientRect();
    }
    else {
        legacyBox = elementOrBox;
    }
    return {
        top: legacyBox.top,
        left: legacyBox.left,
        width: legacyBox.width,
        height: legacyBox.height,
        bottom: legacyBox.top + legacyBox.height,
        right: legacyBox.left + legacyBox.width,
    };
}
function _clamp(min, max, value) {
    // NOTE(mikcol): keeping this impl for legacy behavior
    return Math.min(Math.max(min, value), max);
}
function _calculateTop(targetBounds, sourceBounds, constraintBounds, arrowSize) {
    return {
        left: targetBounds.width / 2 + targetBounds.left - sourceBounds.width / 2,
        top: _clamp(constraintBounds.top, Math.max(0, constraintBounds.top + constraintBounds.height - sourceBounds.height), targetBounds.top - (sourceBounds.height + arrowSize)),
        maxHeight: targetBounds.top - PADDING,
        maxWidth: constraintBounds.width - PADDING * 2,
        constraintHeight: constraintBounds.height - PADDING,
        constraintLeft: constraintBounds.left + PADDING,
        constraintTop: constraintBounds.top + PADDING,
        constraintWidth: constraintBounds.width - PADDING * 2,
    };
}
function _calculateBottom(targetBounds, sourceBounds, constraintBounds, arrowSize) {
    var top = _clamp(constraintBounds.top, constraintBounds.top + constraintBounds.height + arrowSize, targetBounds.top + targetBounds.height);
    return {
        left: targetBounds.width / 2 + targetBounds.left - sourceBounds.width / 2,
        top: top,
        maxHeight: constraintBounds.height + constraintBounds.top - top - PADDING,
        maxWidth: constraintBounds.width - PADDING * 2,
        constraintHeight: constraintBounds.height - PADDING,
        constraintLeft: constraintBounds.left + PADDING,
        constraintTop: constraintBounds.top + PADDING,
        constraintWidth: constraintBounds.width - PADDING * 2,
    };
}
function _calculateLeft(targetBounds, sourceBounds, constraintBounds, arrowSize) {
    return {
        left: _clamp(PADDING, targetBounds.left, targetBounds.left - (sourceBounds.width + arrowSize)),
        top: targetBounds.height / 2 + targetBounds.top - sourceBounds.height / 2,
        maxHeight: constraintBounds.height - PADDING * 2,
        maxWidth: (targetBounds.left - constraintBounds.left) - PADDING,
        constraintHeight: constraintBounds.height - PADDING * 2,
        constraintLeft: constraintBounds.left + PADDING,
        constraintTop: constraintBounds.top + PADDING,
        constraintWidth: constraintBounds.width - PADDING,
    };
}
function _calculateRight(targetBounds, sourceBounds, constraintBounds, arrowSize) {
    var left = _clamp(PADDING, constraintBounds.left + constraintBounds.width + arrowSize, targetBounds.left);
    return {
        left: left,
        top: targetBounds.height / 2 + targetBounds.top - sourceBounds.height / 2,
        maxHeight: constraintBounds.height - PADDING * 2,
        maxWidth: constraintBounds.width + constraintBounds.left - left - PADDING,
        constraintHeight: constraintBounds.height - PADDING * 2,
        constraintLeft: constraintBounds.left + PADDING,
        constraintTop: constraintBounds.top + PADDING,
        constraintWidth: constraintBounds.width - PADDING,
    };
}
/**
 * Positions item (source) around target in relation to the constrained size.
 *     The target and constraint can be an Element or Object.
 *     If constraint is an object, then it assumes that these are global coordinates with the properties:
 *         {
 *             left: Number,
 *             top: Number,
 *             width: Number,
 *             height: Number
 *         }
 *
 * @param {Element} source The HTML Element that is being positioned.
 * @param {(Element|Object)} target What the source is being fit around.
 * @param {(Element|Object)} constraint The bounds that the target is constrained by. If constraint is null then the document body is used.
 * @param {Array.<Position>} precedence The fit algorithm.
 * @returns {Object} The coordinates for the new position of the source element.
 */
export function position(source, target, constraint, precedence, arrowSize) {
    var sourceBounds = getBoundingBox(source);
    var targetBounds = getBoundingBox(target);
    var constraintBounds = getBoundingBox(constraint);
    var fit = null;
    var largestPosition = Position.TOP;
    {
        var maxHeight = void 0, maxWidth = void 0;
        var largestSize = 0;
        var size = {
            width: 0,
            height: 0,
        };
        for (var i = 0, len = precedence.length; !fit && i < len; ++i) {
            size.width = constraintBounds.width;
            size.height = constraintBounds.height;
            switch (precedence[i]) {
                case Position.TOP:
                    maxHeight = targetBounds.top - constraintBounds.top - PADDING;
                    if (maxHeight >= sourceBounds.height + arrowSize) {
                        fit = _calculateTop(targetBounds, sourceBounds, constraintBounds, arrowSize);
                        largestPosition = Position.TOP;
                    }
                    size.height = maxHeight;
                    break;
                case Position.BOTTOM:
                    maxHeight =
                        constraintBounds.height - PADDING - (targetBounds.top - constraintBounds.top + targetBounds.height);
                    if (maxHeight >= sourceBounds.height + arrowSize) {
                        fit = _calculateBottom(targetBounds, sourceBounds, constraintBounds, arrowSize);
                        largestPosition = Position.BOTTOM;
                    }
                    size.height = maxHeight;
                    break;
                case Position.LEFT:
                    maxWidth = targetBounds.left - constraintBounds.left;
                    if (maxWidth >= sourceBounds.width + arrowSize - PADDING) {
                        fit = _calculateLeft(targetBounds, sourceBounds, constraintBounds, arrowSize);
                        largestPosition = Position.LEFT;
                    }
                    size.width = maxWidth;
                    break;
                case Position.RIGHT:
                    maxWidth =
                        constraintBounds.width - PADDING - (targetBounds.left - constraintBounds.left + targetBounds.width);
                    if (maxWidth >= sourceBounds.width + arrowSize - PADDING) {
                        fit = _calculateRight(targetBounds, sourceBounds, constraintBounds, arrowSize);
                        largestPosition = Position.RIGHT;
                    }
                    size.width = maxWidth;
                    break;
                default:
                    console.error("[ltjs-commons]: invalid position precedence value: ".concat(precedence[i]));
                    continue;
            }
            var weightedSize = size.width * WIDTH_WEIGHT * size.height;
            if (weightedSize > largestSize) {
                largestSize = weightedSize;
                largestPosition = precedence[i];
            }
        }
    }
    if (!fit) {
        switch (largestPosition) {
            case Position.TOP:
                fit = _calculateTop(targetBounds, sourceBounds, constraintBounds, arrowSize);
                break;
            case Position.BOTTOM:
                fit = _calculateBottom(targetBounds, sourceBounds, constraintBounds, arrowSize);
                break;
            case Position.LEFT:
                fit = _calculateLeft(targetBounds, sourceBounds, constraintBounds, arrowSize);
                break;
            case Position.RIGHT:
                fit = _calculateRight(targetBounds, sourceBounds, constraintBounds, arrowSize);
                break;
        }
    }
    if (!fit) {
        return null;
    }
    // NOTE(mikcol): value isn't used
    //const parentOffset = source.offsetParent || document.body;
    // NOTE(mikcol): value isn't used
    //const sourceParentBounds = parentOffset.getBoundingClientRect();
    // min = left constraint, max = max(left constraint, remaining width), value = min()
    var left = _clamp(fit.constraintLeft, Math.max(fit.constraintLeft, fit.constraintLeft + fit.constraintWidth - targetBounds.width), Math.min(fit.constraintWidth + fit.constraintLeft - sourceBounds.width, fit.left));
    var top = _clamp(fit.constraintTop, Math.max(fit.constraintTop, fit.constraintTop + fit.constraintHeight - targetBounds.height), Math.min(fit.constraintHeight + fit.constraintTop - sourceBounds.height, fit.top));
    return {
        fit: largestPosition,
        fitPosition: fit,
        offset: {
            left: left,
            top: top,
        },
    };
}
